import {
  getPack,
  isVisible,
  normalize,
  validateAll,
  type FormDoc,
  type FormPack,
  type RuleContext,
  type SaveValuesResult,
  type Validation,
  type Value,
  type Values,
} from "@gsignatures/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApiFailure, SignerApi } from "../api/types";
import { Saver, type SaveStatus } from "./saver";

/** At its last blur a field was fine, or showed an error (so a new error waits for the next blur). */
export type Touched = Record<string, "ok" | "error">;

export interface Answers {
  doc: FormDoc;
  pack: FormPack;
  values: Values;
  ctx: RuleContext;
  validation: Validation;
  touched: Touched;
  /** The question last focused — its box is highlighted on the form. */
  focus: string | null;
  setFocus: (id: string | null) => void;
  /** While typing: saved ~600 ms after the last keystroke. */
  type: (id: string, v: Value) => void;
  /** A finished change (blur, toggle, select): normalised and saved at once. */
  commit: (id: string, v?: Value) => void;
  /** Shows every visible field's message (on Continue). Returns the first field with an error, if any. */
  touchAll: () => string | null;
  /** Saves now; true when the server has the newest answers. */
  flush: () => Promise<boolean>;
}

interface Store {
  key: string | null;
  values: Values;
  touched: Touched;
}

/**
 * The form's answers on this page. They stay here, in memory, whatever the network does;
 * the saver pushes them to the server and retries. Validation is core's, run on every change.
 */
export function useAnswers(api: SignerApi, doc: FormDoc | undefined, onFatal: (e: ApiFailure) => void, onSaved?: (r: SaveValuesResult) => void) {
  const key = doc?.pack ? `${doc.id}|${doc.pack}` : null;
  const [store, setStore] = useState<Store>({ key: null, values: {}, touched: {} });
  const [focus, setFocus] = useState<string | null>(null);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const valuesRef = useRef<Values>(store.values);

  // A newly chosen form (or a different one) starts from the server's answers.
  if (key && store.key !== key && doc) {
    const fresh: Store = { key, values: { ...doc.values }, touched: {} };
    valuesRef.current = fresh.values;
    setStore(fresh);
  }

  const fatal = useRef(onFatal);
  fatal.current = onFatal;
  const saved = useRef(onSaved);
  saved.current = onSaved;
  const docId = doc?.id;
  const saver = useMemo(
    () =>
      docId
        ? new Saver((v) => api.saveValues(docId, v), {
            status: setStatus,
            fatal: (e) => fatal.current(e),
            saved: (r) => saved.current?.(r),
          })
        : null,
    [api, docId],
  );
  useEffect(() => () => saver?.dispose(), [saver]);

  // Leaving the page: push what's waiting, and warn if it hasn't reached the server.
  useEffect(() => {
    if (!saver) return;
    const onHide = () => {
      if (saver.dirty) void saver.flush();
    };
    const onBefore = (e: BeforeUnloadEvent) => {
      if (saver.dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onBefore);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onBefore);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [saver]);

  const pack = doc?.pack ? getPack(doc.pack) : null;
  const policy = doc?.policy;
  const ctx = useMemo<RuleContext>(() => ({ now: new Date(), ...(policy ? { policy } : {}) }), [policy]);
  const validation = useMemo(() => (pack ? validateAll(pack.fields, store.values, ctx) : null), [pack, store.values, ctx]);

  const write = useCallback(
    (id: string, v: Value, now: boolean, markTouched: boolean) => {
      const next = { ...valuesRef.current, [id]: v };
      valuesRef.current = next;
      setStore((s) => {
        let touched = s.touched;
        if (markTouched && pack) {
          const errs = validateAll(pack.fields, next, ctx).byField[id] ?? [];
          touched = { ...s.touched, [id]: errs.some((i) => i.severity === "error") ? "error" : "ok" };
        }
        return { ...s, values: next, touched };
      });
      if (!saver) return;
      if (now) void saver.flush(next);
      else saver.schedule(next);
    },
    [saver, pack, ctx],
  );

  const type = useCallback((id: string, v: Value) => write(id, v, false, false), [write]);

  const commit = useCallback(
    (id: string, v?: Value) => {
      const spec = pack?.fields.find((f) => f.id === id);
      const raw = v === undefined ? valuesRef.current[id] : v;
      write(id, spec ? normalize(spec, raw) : raw, true, true);
    },
    [write, pack],
  );

  const touchAll = useCallback((): string | null => {
    if (!pack) return null;
    const vals = valuesRef.current;
    const res = validateAll(pack.fields, vals, ctx);
    const t: Touched = {};
    let first: string | null = null;
    const order = pack.sections.flatMap((s) => s.fields);
    for (const id of order) {
      const spec = pack.fields.find((f) => f.id === id);
      if (!spec || !isVisible(spec, vals)) continue;
      const err = (res.byField[id] ?? []).some((i) => i.severity === "error");
      t[id] = err ? "error" : "ok";
      if (err && !first) first = id;
    }
    setStore((s) => ({ ...s, touched: { ...s.touched, ...t } }));
    return first;
  }, [pack, ctx]);

  const flush = useCallback(async () => (saver ? saver.flush(valuesRef.current) : true), [saver]);

  const answers: Answers | null =
    doc && pack && validation && store.key === key
      ? { doc, pack, values: store.values, ctx, validation, touched: store.touched, focus, setFocus, type, commit, touchAll, flush }
      : null;

  return { answers, saveStatus: status };
}
